import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Skeleton from "../components/Skeleton";
import { getAuthToken } from "../services/apiClient";
import {
  assignAdmin,
  connectChatSocket,
  fetchConversations,
  fetchMessages,
  joinConversation,
  normalizePresencePayload,
  normalizeSocketMessage,
  sendMessage,
} from "../services/chatService";

const getStoredAdmin = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "{}");
  } catch (error) {
    return {};
  }
};

const getAdminId = () => {
  const admin = getStoredAdmin();

  return admin.id || admin.userId || admin.adminId;
};

const formatTime = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelativeTime = (dateValue, nowValue = Date.now()) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  const diffInSeconds = Math.max(
    0,
    Math.floor((nowValue - date.getTime()) / 1000),
  );

  if (diffInSeconds < 10) return "just now";
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);

  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  return formatDateLabel(dateValue);
};

const formatConversationTitle = (conversation) => {
  return `Conversation #${conversation.id}`;
};

const getCustomerDetails = (conversation = {}, nowValue = Date.now()) => {
  const customer =
    conversation.user ||
    conversation.customer ||
    conversation.patient ||
    conversation.userDetails ||
    {};
  const id = conversation.userId || customer.id || customer.userId;
  const presenceStatus =
    customer.status ||
    customer.presence ||
    customer.connectionStatus ||
    conversation.presence ||
    conversation.connectionStatus;
  const name =
    customer.name ||
    customer.fullName ||
    customer.full_name ||
    conversation.customerName ||
    conversation.userName ||
    `Customer #${id || "-"}`;
  const email = customer.email || conversation.email || conversation.customerEmail;
  const phone =
    customer.phone ||
    customer.phoneNumber ||
    customer.mobile ||
    conversation.phone ||
    conversation.customerPhone;
  const explicitOnline =
    typeof customer.isOnline === "boolean"
      ? customer.isOnline
      : typeof customer.is_online === "boolean"
        ? customer.is_online
        : typeof customer.online === "boolean"
          ? customer.online
          : typeof conversation.isOnline === "boolean"
            ? conversation.isOnline
            : typeof conversation.is_online === "boolean"
              ? conversation.is_online
              : undefined;
  const lastMessageAt =
    conversation.lastMessageAt ||
    conversation.updatedAt ||
    conversation.createdAt;
  const lastSeenAt =
    customer.lastSeenAt ||
    customer.last_seen_at ||
    conversation.lastSeenAt ||
    conversation.last_seen_at ||
    lastMessageAt;
  const recentActivity = (() => {
    if (!lastMessageAt) return false;
    const lastTime = new Date(lastMessageAt).getTime();
    if (Number.isNaN(lastTime)) return false;
    return nowValue - lastTime <= 3 * 60 * 1000;
  })();
  const isOnline =
    typeof explicitOnline === "boolean"
      ? explicitOnline
      : typeof presenceStatus === "string"
        ? presenceStatus.toLowerCase() === "online"
        : recentActivity;

  return {
    id,
    name,
    email,
    phone,
    isOnline,
    lastSeenAt,
  };
};

const getInitials = (name) => {
  const parts = name
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return "U";

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const formatDateLabel = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const shouldShowDateDivider = (messages, index) => {
  if (index === 0) return true;

  const currentDate = new Date(messages[index].createdAt).toDateString();
  const previousDate = new Date(messages[index - 1].createdAt).toDateString();

  return currentDate !== previousDate;
};

const formatPresenceLabel = (customer, nowValue) => {
  if (customer.isOnline) return "Online now";
  if (customer.lastSeenAt) {
    return `Last seen ${formatRelativeTime(customer.lastSeenAt, nowValue)}`;
  }

  return "Offline";
};

const sortConversationsByActivity = (conversationList) => {
  return [...conversationList].sort((first, second) => {
    const firstTime = new Date(
      first.lastMessageAt || first.updatedAt || first.createdAt || 0,
    ).getTime();
    const secondTime = new Date(
      second.lastMessageAt || second.updatedAt || second.createdAt || 0,
    ).getTime();

    return secondTime - firstTime;
  });
};

const hasConversationMessages = (conversation = {}) => {
  const messageCount =
    conversation.messageCount ??
    conversation.messagesCount ??
    conversation.message_count ??
    conversation.messages_count ??
    conversation.totalMessages ??
    conversation.total_messages;
  const lastMessage =
    conversation.lastMessage ||
    conversation.last_message ||
    conversation.latestMessage ||
    conversation.latest_message ||
    conversation.recentMessage ||
    conversation.recent_message;
  const lastMessageId =
    conversation.lastMessageId ||
    conversation.last_message_id ||
    lastMessage?.id ||
    lastMessage?._id;
  const lastMessageText =
    typeof lastMessage === "string"
      ? lastMessage
      : lastMessage?.message ||
        lastMessage?.body ||
        lastMessage?.text ||
        lastMessage?.content;
  const lastMessageAt =
    conversation.lastMessageAt ||
    conversation.last_message_at ||
    lastMessage?.createdAt ||
    lastMessage?.created_at ||
    lastMessage?.timestamp;

  if (Number(messageCount) > 0) return true;
  if (lastMessageId) return true;
  if (typeof lastMessageText === "string" && lastMessageText.trim()) return true;

  return Boolean(lastMessageAt && (lastMessageId || lastMessageText));
};

const getConversationLastMessageText = (conversation = {}) => {
  const lastMessage =
    conversation.lastMessage ||
    conversation.last_message ||
    conversation.latestMessage ||
    conversation.latest_message ||
    conversation.recentMessage ||
    conversation.recent_message;

  if (typeof lastMessage === "string") return lastMessage;

  return (
    lastMessage?.message ||
    lastMessage?.body ||
    lastMessage?.text ||
    lastMessage?.content ||
    ""
  );
};

const getConversationLastMessageAt = (conversation = {}) => {
  const lastMessage =
    conversation.lastMessage ||
    conversation.last_message ||
    conversation.latestMessage ||
    conversation.latest_message ||
    conversation.recentMessage ||
    conversation.recent_message;

  return (
    conversation.lastMessageAt ||
    conversation.last_message_at ||
    conversation.updatedAt ||
    conversation.updated_at ||
    lastMessage?.createdAt ||
    lastMessage?.created_at ||
    lastMessage?.timestamp
  );
};

const upsertConversationActivity = (
  conversationList,
  normalizedMessage,
  currentAdminId,
) => {
  const existingConversation = conversationList.find(
    (conversation) => conversation.id === normalizedMessage.conversationId,
  );

  const nextConversation = existingConversation
    ? {
        ...existingConversation,
        lastMessage: normalizedMessage.message,
        lastMessageAt: normalizedMessage.createdAt,
        updatedAt: normalizedMessage.createdAt,
      }
    : {
        id: normalizedMessage.conversationId,
        userId:
          Number(normalizedMessage.senderId) === Number(currentAdminId)
            ? undefined
            : normalizedMessage.senderId,
        assignedAdminId: currentAdminId,
        status: "open",
        lastMessage: normalizedMessage.message,
        lastMessageAt: normalizedMessage.createdAt,
        createdAt: normalizedMessage.createdAt,
        updatedAt: normalizedMessage.createdAt,
      };

  return sortConversationsByActivity([
    nextConversation,
    ...conversationList.filter(
      (conversation) => conversation.id !== normalizedMessage.conversationId,
    ),
  ]);
};

const applyPresenceToConversation = (conversation, presence) => {
  const customer = getCustomerDetails(conversation);

  if (Number(customer.id) !== Number(presence.userId)) return conversation;

  return {
    ...conversation,
    isOnline: presence.isOnline,
    lastSeenAt: presence.lastSeenAt || conversation.lastSeenAt,
    customer: {
      ...(conversation.customer || {}),
      isOnline: presence.isOnline,
      lastSeenAt: presence.lastSeenAt || conversation.customer?.lastSeenAt,
    },
    user: {
      ...(conversation.user || {}),
      isOnline: presence.isOnline,
      lastSeenAt: presence.lastSeenAt || conversation.user?.lastSeenAt,
    },
  };
};

const Chat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [socket, setSocket] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState("");
  const [socketConnected, setSocketConnected] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  const chatEndRef = useRef(null);
  const conversationsRef = useRef([]);
  const joinedConversationRef = useRef(null);
  const selectedConversationIdRef = useRef(null);
  const loadConversationsRef = useRef(null);
  const adminIdRef = useRef(null);
  const token = getAuthToken();
  const adminId = getAdminId();

  useEffect(() => {
    adminIdRef.current = adminId;
  }, [adminId]);

  const loadConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      setError("");

      const conversationList = await fetchConversations();
      const normalizedConversations = Array.isArray(conversationList)
        ? sortConversationsByActivity(
            conversationList.filter(hasConversationMessages),
          )
        : [];

      setConversations(normalizedConversations);

      setSelectedConversation((previousConversation) =>
        previousConversation
          ? normalizedConversations.find(
              (conversation) => conversation.id === previousConversation.id,
            ) || previousConversation
          : normalizedConversations[0] || null,
      );
    } catch (loadError) {
      setError(loadError.message || "Unable to load conversations.");
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    loadConversationsRef.current = loadConversations;
  }, [loadConversations]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const requestedConversationId = searchParams.get("conversation");
    if (!requestedConversationId || !conversations.length) return;

    const conversation = conversations.find(
      (item) => String(item.id ?? item._id ?? item.conversationId) === requestedConversationId,
    );
    if (!conversation) return;

    setSelectedConversation(conversation);
    setSearchParams({}, { replace: true });
  }, [conversations, searchParams, setSearchParams]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    selectedConversationIdRef.current = selectedConversation?.id || null;
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (!token) return undefined;

    const chatSocket = connectChatSocket(token);
    if (!chatSocket) return undefined;

    const handleConnect = () => {
      setSocketConnected(true);
      loadConversations();

      if (selectedConversationIdRef.current) {
        joinConversation(chatSocket, selectedConversationIdRef.current);
        joinedConversationRef.current = selectedConversationIdRef.current;
      }
    };

    const handleDisconnect = () => setSocketConnected(false);

    const handleConnectError = () => {
      setSocketConnected(false);
      setError("Unable to connect to chat socket.");
    };

    chatSocket.on("connect", handleConnect);
    chatSocket.on("disconnect", handleDisconnect);
    chatSocket.on("connect_error", handleConnectError);

    setSocket(chatSocket);

    return () => {
      chatSocket.off("connect", handleConnect);
      chatSocket.off("disconnect", handleDisconnect);
      chatSocket.off("connect_error", handleConnectError);
      setSocket(null);
      setSocketConnected(false);
    };
  }, [loadConversations, token]);

  useEffect(() => {
    if (!selectedConversation?.id) {
      setMessages([]);
      return;
    }

    const loadSelectedMessages = async () => {
      try {
        setLoadingMessages(true);
        setError("");

        if (adminId) {
          await assignAdmin({
            conversationId: selectedConversation.id,
            adminId,
          });
        }

        const messageList = await fetchMessages(selectedConversation.id);
        setMessages(Array.isArray(messageList) ? messageList : []);

        if (socket && joinedConversationRef.current !== selectedConversation.id) {
          joinConversation(socket, selectedConversation.id);
          joinedConversationRef.current = selectedConversation.id;
        }
      } catch (loadError) {
        setError(loadError.message || "Unable to load messages.");
      } finally {
        setLoadingMessages(false);
      }
    };

    loadSelectedMessages();
  }, [adminId, selectedConversation?.id, socket]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleNewMessage = (incomingMessage) => {
      const normalizedMessage = normalizeSocketMessage(incomingMessage);
      setCurrentTime(Date.now());
      const hasConversation = conversationsRef.current.some(
        (conversation) => conversation.id === normalizedMessage.conversationId,
      );

      setConversations((previousConversations) => {
        return upsertConversationActivity(
          previousConversations,
          normalizedMessage,
          adminIdRef.current,
        );
      });

      if (!hasConversation) {
        loadConversationsRef.current?.();
      }

      setSelectedConversation((previousConversation) =>
        previousConversation?.id === normalizedMessage.conversationId
          ? {
              ...previousConversation,
              lastMessage: normalizedMessage.message,
              lastMessageAt: normalizedMessage.createdAt,
              updatedAt: normalizedMessage.createdAt,
            }
          : previousConversation,
      );

      if (normalizedMessage.conversationId === selectedConversationIdRef.current) {
        setMessages((previousMessages) => {
          const alreadyExists = previousMessages.some(
            (message) => message.id === normalizedMessage.id,
          );

          return alreadyExists
            ? previousMessages
            : [...previousMessages, normalizedMessage];
        });
      }
    };

    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("new_message", handleNewMessage);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return undefined;

    const handlePresenceUpdate = (payload, fallbackOnline) => {
      const presence = normalizePresencePayload(payload, fallbackOnline);

      if (!presence.userId) return;

      setConversations((previousConversations) =>
        previousConversations.map((conversation) =>
          applyPresenceToConversation(conversation, presence),
        ),
      );

      setSelectedConversation((previousConversation) =>
        previousConversation
          ? applyPresenceToConversation(previousConversation, presence)
          : previousConversation,
      );
    };

    const handleOnline = (payload) => handlePresenceUpdate(payload, true);
    const handleOffline = (payload) =>
      handlePresenceUpdate(
        {
          ...payload,
          lastSeenAt: payload?.lastSeenAt || payload?.last_seen_at || new Date().toISOString(),
        },
        false,
      );
    const handleStatus = (payload) => handlePresenceUpdate(payload);

    socket.on("user_online", handleOnline);
    socket.on("user_offline", handleOffline);
    socket.on("user_status", handleStatus);
    socket.on("presence_update", handleStatus);

    return () => {
      socket.off("user_online", handleOnline);
      socket.off("user_offline", handleOffline);
      socket.off("user_status", handleStatus);
      socket.off("presence_update", handleStatus);
    };
  }, [socket]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const handleSendMessage = async () => {
    const trimmedMessage = messageInput.trim();

    if (!trimmedMessage || !selectedConversation?.id || !socket) return;

    try {
      setError("");
      const sentMessage = await sendMessage(
        socket,
        selectedConversation.id,
        trimmedMessage,
      );

      const normalizedMessage = normalizeSocketMessage(sentMessage);
      setCurrentTime(Date.now());

      setConversations((previousConversations) =>
        upsertConversationActivity(
          previousConversations,
          normalizedMessage,
          adminId,
        ),
      );

      setSelectedConversation((previousConversation) =>
        previousConversation?.id === normalizedMessage.conversationId
          ? {
              ...previousConversation,
              lastMessage: normalizedMessage.message,
              lastMessageAt: normalizedMessage.createdAt,
              updatedAt: normalizedMessage.createdAt,
            }
          : previousConversation,
      );

      if (normalizedMessage.conversationId === selectedConversation?.id) {
        setMessages((previousMessages) => {
          const alreadyExists = previousMessages.some(
            (message) => message.id === normalizedMessage.id,
          );

          return alreadyExists
            ? previousMessages
            : [...previousMessages, normalizedMessage];
        });
      }
    } catch (sendError) {
      setError(sendError.message || "Unable to send message.");
    } finally {
      setMessageInput("");
    }
  };

  const selectedCustomer = getCustomerDetails(selectedConversation || {}, currentTime);
  const selectedInitials = getInitials(selectedCustomer.name);

  return (
    <div className="main-wrapper chat-shell">
      <div className="page-wrapper">
        <div className="page-content chat-page">
          <div className="row g-4 chat-workspace-row">
            <div className="col-lg-4">
              <div className="card rounded-4 chat-sidebar-card">
                <div className="section-title p-3 border-bottom d-flex align-items-center justify-content-between">
                  <div>
                    <h5>Customer Chats</h5>
                    <p className="small text-muted mb-0">Live support inbox</p>
                  </div>
                  <span
                    className={`chat-connection-pill ${
                      socketConnected ? "is-online" : ""
                    }`}
                  >
                    {socketConnected ? "Online" : "Offline"}
                  </span>
                </div>

                <div className="messaging-list">
                  <div className="user-list-messaging">
                    {loadingConversations && (
                      <>
                        {[1, 2, 3].map((item) => (
                          <button
                            key={item}
                            type="button"
                            className="list-items chat-conversation-item p-3 border-top"
                            disabled
                          >
                            <div className="d-flex align-items-start gap-3">
                              <div className="user-list chat-user-initial">
                                <Skeleton circle width="40px" height="40px" />
                              </div>
                              <div className="user-list-messing flex-grow-1">
                                <Skeleton width="50%" className="mb-2" />
                                <Skeleton width="70%" className="mb-2" />
                                <Skeleton width="90%" />
                              </div>
                            </div>
                          </button>
                        ))}
                      </>
                    )}

                    {!loadingConversations && conversations.length === 0 && (
                      <div className="chat-empty-state">
                        <strong>No conversations found</strong>
                        <span>New customer messages will appear here.</span>
                      </div>
                    )}

                    {conversations.map((conversation) => {
	                      const customer = getCustomerDetails(conversation, currentTime);
	                      const initials = getInitials(customer.name);
	                      const lastMessageText = getConversationLastMessageText(conversation);

	                      return (
                        <button
                          key={conversation.id}
                          type="button"
                          onClick={() => handleSelectConversation(conversation)}
                          className={`list-items chat-conversation-item p-3 border-top ${
                            selectedConversation?.id === conversation.id ? "active" : ""
                          }`}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <div className="user-list chat-user-initial">
                              <span>{initials}</span>
                              <i
                                className={`chat-presence-dot ${
                                  customer.isOnline ? "is-online" : ""
                                }`}
                                aria-hidden="true"
                              />
                            </div>

                            <div className="user-list-messing flex-grow-1">
                              <div className="d-flex align-items-center justify-content-between gap-2">
                                <div className="d-flex align-items-center gap-2 min-width-0">
                                  <h6>{customer.name}</h6>
                                  {customer.isOnline && (
                                    <span className="chat-online-label">Online</span>
                                  )}
                                </div>
                                <span
                                  className={`chat-status-badge status-${
                                    conversation.status || "open"
                                  }`}
                                >
                                  {conversation.status || "open"}
                                </span>
                              </div>

                              <p className="small chat-customer-meta">
                                {formatPresenceLabel(customer, currentTime)} · User #
                                {customer.id || "-"} · Admin #
                                {conversation.assignedAdminId || "-"}
	                              </p>
	                              <p className="small chat-last-message">
	                                {lastMessageText}
	                              </p>
	                            </div>
                          </div>

                          <div className="chat-time">
	                            {formatRelativeTime(
	                              getConversationLastMessageAt(conversation),
	                              currentTime,
	                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="card rounded-4 chat-thread-card">
              <div className="chat-profile-header">
                <div className="section-title p-3 border-bottom d-flex align-items-center justify-content-between gap-3">
                  {selectedConversation ? (
                    <>
                      <div className="d-flex align-items-center gap-3 min-width-0">
                        <div className="user-list chat-user-initial chat-header-avatar">
                          <span>{selectedInitials}</span>
                          <i
                            className={`chat-presence-dot ${
                              selectedCustomer.isOnline ? "is-online" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="min-width-0">
                          <div className="d-flex align-items-center gap-2 min-width-0">
                            <h5>{selectedCustomer.name}</h5>
                            {selectedCustomer.isOnline && (
                              <span className="chat-online-label">Online</span>
                            )}
                          </div>
                          <p className="small text-muted mb-0 chat-profile-subtitle">
                            {formatConversationTitle(selectedConversation)} · User #
                            {selectedCustomer.id || "-"} ·{" "}
                            {formatPresenceLabel(selectedCustomer, currentTime)}
                          </p>
                        </div>
                      </div>

                      <div className="chat-profile-details">
                        <span>{selectedCustomer.phone || "Phone unavailable"}</span>
                        <span>{selectedCustomer.email || "Email unavailable"}</span>
                        {selectedConversation.status && selectedConversation.status !== "open" && (
                          <span
                            className={`chat-status-badge status-${selectedConversation.status}`}
                          >
                            {selectedConversation.status}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div>
                      <h5>Select a conversation</h5>
                      <p className="small text-muted mb-0">
                        Open a customer chat from the inbox.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  {error}
                </div>
              )}

              <div className="chat-messaging p-4">
                {!selectedConversation && (
                  <div className="chat-empty-state chat-empty-state-large">
                    <strong>No active conversation</strong>
                    <span>Choose a customer from the left panel to view their messages.</span>
                  </div>
                )}

                {selectedConversation && loadingMessages && (
                  <>
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="chat-left-right mb-4">
                        <div className="user-list chat-user-initial chat-message-avatar">
                          <Skeleton circle width="40px" height="40px" />
                        </div>
                        <div className="user-list-messing chat-bubble-column">
                          <Skeleton width="35%" className="mb-2" />
                          <Skeleton width="100%" height="3rem" className="mb-2" />
                          <Skeleton width="25%" />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {selectedConversation && !loadingMessages && messages.length === 0 && (
                  <div className="chat-empty-state chat-empty-state-large">
                    <strong>No messages yet</strong>
                    <span>This conversation is assigned and ready for a reply.</span>
                  </div>
                )}

                {selectedConversation &&
                  !loadingMessages &&
                  messages.map((chatMessage, index) => {
                    const isMyMessage = Number(chatMessage.senderId) === Number(adminId);
                    const showDateDivider = shouldShowDateDivider(messages, index);

                    return (
                      <React.Fragment key={chatMessage.id}>
                        {showDateDivider && (
                          <div className="chat-date-divider">
                            <span>{formatDateLabel(chatMessage.createdAt)}</span>
                          </div>
                        )}
                        <div
                          className={`chat-left-right mb-4 ${
                            isMyMessage ? "chat-end-right" : ""
                          }`}
                        >
                          {!isMyMessage && (
                            <div className="user-list chat-user-initial chat-message-avatar">
                              <span>{selectedInitials}</span>
                            </div>
                          )}

                          <div className="user-list-messing chat-bubble-column">
                            <h6>
                              {isMyMessage ? "Admin" : selectedCustomer.name}
                            </h6>

                            <div className="chat-message-section">
                              <p>{chatMessage.message}</p>
                              <div className="chat-message-meta">
                                <span>{formatTime(chatMessage.createdAt)}</span>
                                {isMyMessage && (
                                  <span>{chatMessage.status || "sent"}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {isMyMessage && (
                            <div className="user-list chat-user-initial chat-message-avatar">
                              <span>A</span>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}

                <div ref={chatEndRef}></div>
              </div>

              <div className="start-chat">
                <button
                  type="button"
                  className="chat-tool-button"
                  disabled={!selectedConversation}
                  aria-label="Add attachment"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                </button>
                <div className="chat-input-wrapper">
                  <input
                    type="text"
                    className="form-control chat-input-field"
                    placeholder={
                      selectedConversation ? "Type here..." : "Select a conversation first"
                    }
                    value={messageInput}
                    disabled={!selectedConversation || !socketConnected}
                    onChange={(event) => setMessageInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                </div>

                <div className="send-message">
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim() || !selectedConversation || !socketConnected}
                    aria-label="Send message"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-send-icon lucide-send"
                    >
                      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                      <path d="m21.854 2.147-10.94 10.939" />
                    </svg>
                  </button>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
